import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { TranslateService } from '@ngx-translate/core';
import { InventoriesService, RetailersService, RetailerNotificationRequest, Filter, FilterRequestForCombo, BrandsService } from '@src/app/shared/backend/clients';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { ExportService } from '@src/app/shared/services/export.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { emailRegexValidator } from '@src/app/shared/validators/email.validator';
import { SessionService } from '@src/app/shared/services/session.service';
import { FiltersPanelChangeEvent, FiltersPanelFieldOverrides, FiltersPanelDropdownLazyLoadEvent } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { buildRemoteDropdownRequest, normalizeDropdownState } from '@src/app/shared/components/filters-panel/filters-panel-dropdown.helpers';
import { FiltersPanelComponent } from '@src/app/shared/components/filters-panel/filters-panel.component';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';
import { SortConfig } from '@src/app/shared/components/sort-panel/custom-sort-panel.model';
import { FriendlyBooleanPipe } from '@src/app/shared/pipes/friendlyBoolean.pipe';

@Component({
    selector: 'app-inventories',
    templateUrl: './inventories.component.html',
    styleUrl: './inventories.component.scss',
    host: {
        class: 'app-inventories'
    },
    standalone: false
})
export class InventoriesComponent extends BaseListComponent implements OnInit {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();

  @ViewChild('inventoriesFiltersPanel') inventoriesFiltersPanel?: FiltersPanelComponent;
  @ViewChild('pendingFiltersPanel') pendingFiltersPanel?: FiltersPanelComponent;

  public readonly inventoriesPanelTableName = TableSettingService.InventoriesTableName + '::tab';
  public readonly pendingPanelTableName = TableSettingService.PendingInventoriesTableName + '::tab';

  filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  activeTabIndex: number = 0;
  tabs: any;

  displayEmailModalbox = false;
  sendEmail = false;
  emailForm!: FormGroup;

  inventoriesColumns: any[] = [];
  pendingColumns: any[] = [];

  inventoriesFilter: Filter[] = [];
  pendingFilter: Filter[] = [];
  invalidatedOptions: Array<{ id: string | number; text: string }> = [];

  inventoriesPageSize: number = 10;
  inventoriesPageNumber: number = 1;

  pendingPageSize: number = 10;
  pendingPageNumber: number = 1;

  exports: boolean = false;
  allowCreateInventory: boolean = false;
  allowRetailerInventories: boolean = false;
  currentDate: Date = new Date();
  displayExportModalbox: boolean = false;
  manualInventory: any;

  brands = {
    results: [],
    pageIndex: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public permissionService: PermissionService,
    public retailersService: RetailersService,
    private fb: FormBuilder,
    public override tableSettingService: TableSettingService,
    private translate: TranslateService,
    public session: SessionService,
    private toasterService: ToasterService,
    private exportService: ExportService,
    private inventoryService: InventoriesService,
    private translateService: TranslateService,
    private brandService: BrandsService) {
      super();
      this.emailForm = this.fb.group({
        id: [null],
        email: ['', [Validators.required, emailRegexValidator(), Validators.maxLength(50)]],
      });
    }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStatesFromStorage();

    this.exports = await this.permissionService.hasActionPermissions(Actions.export_inventories);
    this.allowCreateInventory = await this.permissionService.hasActionPermissions(Actions.create_inventories);
    this.allowRetailerInventories = await this.permissionService.hasActionPermissions(Actions.retailer_inventories);

    await this.tableSettingService.init();

    const company = await this.session.getCompanyData();
    this.manualInventory =
      this.allowRetailerInventories &&
      !!company.inventorySubmissionThresholdDays &&
      company.inventorySubmissionThresholdDays > 0;

    this.inventoriesColumns = await this.tableSettingService.getTableDefaults(TableSettingService.InventoriesTableName);
    this.pendingColumns = this.manualInventory
      ? await this.tableSettingService.getTableDefaults(TableSettingService.PendingInventoriesTableName)
      : [];

    this.tabs = [
      { label: 'INVENTORIES.TABS.INVENTORIES_LIST', route: 'list' as const },
      ...(this.manualInventory
        ? [{ label: 'INVENTORIES.TABS.PENDING_INVENTORIES', route: 'pending' as const }]
        : [])
    ];

    // prepare initial tab context (pagination + sort options) before the user can open the sort panel
    this.applyTabPagination(this.activeRoute);
    this.applyActiveTabContextToBaseList();

    // i18n
    this.rebuildInvalidatedOptions();
    this.translate.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildSortPanelColumnLabels();
        this.rebuildInvalidatedOptions();
      });

    // Sync initial tab from URL and load
    this.route.queryParams
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((params) => {
        const tabParam = params['tab'];

        const prevIndex = this.activeTabIndex;
        const nextIndex = (this.manualInventory && tabParam === 'pending') ? 1 : 0;

        this.activeTabIndex = nextIndex;
        this.applyTabPagination(this.activeRoute);

        const tabChanged = nextIndex !== prevIndex;

        if (tabChanged) {
          this.initialLoadDone = false;
        }

        this.applyActiveTabContextToBaseList();
        this.rebuildSortPanelColumnLabels();

        if (this.initialLoadDone && !tabChanged) {
          return;
        }

        // fallback if restore/ init not triggered
        setTimeout(async () => {
          if (!this.initialLoadDone) {
            this.initialLoadDone = true;
            await this.loadActiveTab();
          }
        });
      });
  }

  private rebuildInvalidatedOptions() {
      const pipe = new FriendlyBooleanPipe(this.translateService);
      this.invalidatedOptions = pipe.values;
      this.rebuildFiltersPanelOverrides();
    }

  private applyActiveTabContextToBaseList() {
    const tab = this.activeRoute;

    let storageTableName: string;
    let visibleColumns: any[];

    if (tab === 'pending') {
      storageTableName = this.pendingPanelTableName;
      visibleColumns = this.pendingColumns;
    } else {
      storageTableName = this.inventoriesPanelTableName;
      visibleColumns = this.inventoriesColumns;
    }

    // tableName per-tab
    this.tableName = storageTableName;

    this.sortPanelStorageTableName = storageTableName;

    // sort panel uses the visible columns for the dropdown
    this.tableColumns = visibleColumns;
    this.allTableColumns = Array.isArray(visibleColumns) ? visibleColumns : [];

    let defaultSort: SortConfig;

    if (tab === 'pending') {
      defaultSort = { key: 'lastInventoryCreationDate', direction: 'DESC' };
    } else {
      defaultSort = { key: 'createdOn', direction: 'DESC' };
    }

    // default sort for stable pagination + 18n fn labelResolver (sort dropdown options)
    this.initSortPanel({
      defaultSort,
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translate.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    const nextSortableColumns = [];

    for (const col of visibleColumns) {
      if (!col) {
        continue;
      }

      const field = String(col.field ?? '').trim();
      if (field.length === 0) {
        continue;
      }

      if (field.toLowerCase() === 'actions') {
        continue;
      }

      if (col?.sortable === false) {
        continue;
      }

      const translationKey = `TABLE.HEADER.${col.header}`;
      const translated = this.translate.instant(translationKey);

      let labelToShow = col.header;
      if (translated && translated !== translationKey) {
        labelToShow = translated;
      }

      nextSortableColumns.push({
        key: field,
        label: labelToShow
      });
    }

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  private rebuildFiltersPanelOverrides() {
    this.filtersPanelFieldOverrides = {
      brandId: {
        fieldLabel: 'TABLE.HEADER.Brand',
        isMultiselect: true,

        options: this.brands.results ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchPlaceholder: 'Search...',
        searchMode: 'remote',

        showSearchbox: true,
        showClear: true,
        compactUi: true,

        pageSize: 10,
        totalCount: this.brands.totalCount ?? 0,
        totalPages: this.brands.totalPages ?? 1,
        pageIndex: this.brands.pageIndex ?? 1,
        hasPreviousPage: this.brands.hasPreviousPage ?? false,
        hasNextPage: this.brands.hasNextPage ?? false
      },
      isInvalid: {
        fieldLabel: 'TABLE.HEADER.IsInvalid',
        isMultiselect: false,

        options: this.invalidatedOptions??
          [
            { text: 'Yes', id: true },
            { text: 'No', id: false }
          ],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      } 
    };
  }

  override async get() {
    if (this.isPendingTab) {
      await this.getPendingInventories();
      return;
    }

    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.inventoryService.apiInventoriesFilterTablePost(this.filter));
    this.data = this.page.results;
  }

  private async getPendingInventories() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.session.setLoading(true);

    this.page = await firstValueFrom(
      this.retailersService.apiRetailersCheckInventoriesSubmissionPost(this.filter)
    );

    this.data = this.page.results;
    this.session.setLoading(false);
  }

  private restorePaginatorStatesFromStorage(): void {
    // List tab
    const listStored = loadStoredPaginatorState(this.inventoriesPanelTableName);
    if (listStored) {
      this.inventoriesPageNumber = listStored.valuesByFieldKey.pageNumber;
      this.inventoriesPageSize = listStored.valuesByFieldKey.pageSize;
    }

    // Pending tab
    const pendingStored = loadStoredPaginatorState(this.pendingPanelTableName);
    if (pendingStored) {
      this.pendingPageNumber = pendingStored.valuesByFieldKey.pageNumber;
      this.pendingPageSize = pendingStored.valuesByFieldKey.pageSize;
    }
  }

  private applyTabPagination(route: 'list' | 'pending'): void {
    if (route === 'pending') {
      this.pageNumber = this.pendingPageNumber;
      this.pageSize = this.pendingPageSize;
    } else {
      this.pageNumber = this.inventoriesPageNumber;
      this.pageSize = this.inventoriesPageSize;
    }

    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;
  }

  private resetPaginatorStorageForTab(route: 'list' | 'pending'): void {
    const tableName = route === 'pending' ? this.pendingPanelTableName : this.inventoriesPanelTableName;

    resetPaginatorStateToFirstPage({
      tableName,
      pageSize: this.pageSize,
    });
  }

  protected override onCriteriaResetRequested(): void {
    const tab = this.activeRoute;

    if (tab === 'pending') {
      this.pendingPageNumber = 1;
    } else {
      this.inventoriesPageNumber = 1;
    }

    this.resetPaginatorStorageForTab(tab);
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent, tab: 'list' | 'pending'): Promise<void> {
    if (tab !== this.activeRoute) {
      return;
    }

    // prevent duplicate load
    if (evt.source === 'restore' && this.initialLoadDone) {
      return;
    }

    const cols = tab === 'pending' ? this.pendingColumns : this.inventoriesColumns;
    const current = this.filter.filters ?? [];

    const next = applyFiltersPanelChange(evt, current as any, cols);
    if (!next) {
      return;
    }

    this.filter.filters = next as any;

    const isApplyOrClear = evt.source === 'apply' || evt.source === 'clear';
    if (isApplyOrClear) {
      this.pageNumber = 1;
      this.filter.pageNumber = 1;

      if (tab === 'pending') this.pendingPageNumber = 1;
      else this.inventoriesPageNumber = 1;

      this.resetPaginatorStorageForTab(tab);
    }

    this.initialLoadDone = true;
    await this.loadActiveTab();
  }

  async onFiltersPanelDropdownLazyLoad(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
      if (!evt) {
        return;
      }

      // fetch dropdowns remote data
      const handlers: Record<string, (e: FiltersPanelDropdownLazyLoadEvent) => Promise<void>> = {
        brandId: (e) => this.loadBrandsCombo(e)
      };

      const handler = handlers[evt.fieldKey];
      if (!handler) {
        return;
      }

      await handler(evt);
  }

  toggleActiveFiltersPanel(): void {
    if (this.isPendingTab) {
      this.pendingFiltersPanel?.toggleOpen();
    } else {
      this.inventoriesFiltersPanel?.toggleOpen();
    }
  }

  getActiveFiltersPanelOpen(): boolean {
    return this.isPendingTab
      ? !!this.pendingFiltersPanel?.isOpen
      : !!this.inventoriesFiltersPanel?.isOpen;
  }

  get isFiltersPanelOpen(): boolean {
    return this.getActiveFiltersPanelOpen();
  }

  private get activeRoute(): 'list' | 'pending' {
    return (this.tabs?.[this.activeTabIndex]?.route ?? 'list') as any;
  }

  get isPendingTab(): boolean {
    return this.manualInventory && this.activeTabIndex === 1;
  }

  private async loadActiveTab() {
    await this.get();
  }

  override async customSort(evt: any, tab: 'list' | 'pending' = this.activeRoute) {
    if (tab !== this.activeRoute) {
      return;
    }

    await super.customSort(evt);
  }

  override async filterEvent(field: string, data: any, operator: string) {
    this.filter.filters ??= [];

    if (!data || data === '') {
      this.filter.filters = this.filter.filters.filter(v => v.propertyName !== field);
    } else {
      if (data?.length > 1 && typeof data[0]?.getMonth === 'function') {
        const start = new Date(Date.UTC(data[0].getFullYear(), data[0].getMonth(), data[0].getDate(), 0, 0, 0, 0));
        const end = new Date(Date.UTC(
          (data[1] ?? data[0]).getFullYear(),
          (data[1] ?? data[0]).getMonth(),
          (data[1] ?? data[0]).getDate(),
          23, 59, 59, 999
        ));
        data = [start.toISOString(), end.toISOString()];
      }

      const existing = this.filter.filters.find(v => v.propertyName === field);
      if (existing) {
        existing.values = Array.isArray(data) ? data : [data];
      } else {
        this.filter.filters.push({ propertyName: field, operator, values: Array.isArray(data) ? data : [data] });
      }
    }

    this.pageNumber = 1;
    this.filter.pageNumber = 1;

    this.initialLoadDone = true;
    await this.loadActiveTab();
  }

  async onTabChange(
    tabValue: string | number | undefined
  ): Promise<void> {
    const nextTabIndex = Number(tabValue ?? 0);

    this.activeTabIndex = nextTabIndex;
    this.initialLoadDone = false;

    if (this.isPendingTab) {
      this.inventoriesFilter = (this.filter.filters ?? []).map(filter => ({
        ...filter
      }));

      this.inventoriesPageSize = this.pageSize;
      this.inventoriesPageNumber = this.pageNumber;

      this.filter.filters = this.pendingFilter.map(filter => ({
        ...filter
      }));

      this.pageNumber = this.pendingPageNumber;
      this.pageSize = this.pendingPageSize;
    } else {
      this.pendingFilter = (this.filter.filters ?? []).map(filter => ({
        ...filter
      }));

      this.pendingPageSize = this.pageSize;
      this.pendingPageNumber = this.pageNumber;

      this.filter.filters = this.inventoriesFilter.map(filter => ({
        ...filter
      }));

      this.pageNumber = this.inventoriesPageNumber;
      this.pageSize = this.inventoriesPageSize;
    }

    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    // Apply the per-tab sort storage key + restore the tab's stored sort setting
    this.applyActiveTabContextToBaseList();
    this.rebuildSortPanelColumnLabels();

    // fallback if restore/ init not triggered
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.loadActiveTab();
      }
    });
  }

  override onPageChange(data: any, tab: 'list' | 'pending' = this.activeRoute) {
    if (tab !== this.activeRoute) {
      return;
    }

    if (!this.initialLoadDone) {
      this.pageNumber = data.page;
      this.pageSize = data.rows;

      if (this.isPendingTab) {
        this.pendingPageNumber = this.pageNumber;
        this.pendingPageSize = this.pageSize;
      } else {
        this.inventoriesPageNumber = this.pageNumber;
        this.inventoriesPageSize = this.pageSize;
      }

      return;
    }

    this.pageNumber = data.page;
    this.pageSize = data.rows;

    if (this.isPendingTab) {
      this.pendingPageNumber = this.pageNumber;
      this.pendingPageSize = this.pageSize;
    } else {
      this.inventoriesPageNumber = this.pageNumber;
      this.inventoriesPageSize = this.pageSize;
    }

    void this.loadActiveTab();
  }

  goToInventoryDetails(event: any, inventoryId: string) {
    this.router.navigate([`/inventories/${inventoryId}`]);
  }

  goToCreateInventoryItemForm() {
    this.router.navigate(['inventories/add']);
  }

  onOpenExportModalbox() {
    this.displayExportModalbox = true;
  }

  handleExportSubmit(form: FormGroup) {
    const range = form.value.dateRange;
    const fileName = form.value.exportName;
    const fileType = form.value.format;

    this.exportService.exportData(
      this.filter,
      [range?.[0], range?.[1] ?? new Date()],
      this.inventoryService.apiInventoriesExportPost.bind(this.inventoryService),
      {
        fileName,
        fileType,
        onSuccess: () => {
          const title = this.translate.instant('EXPORT_SETTINGS.TOAST_SUCCESS_TITLE');
          const message = this.translate.instant('EXPORT_SETTINGS.TOAST_SUCCESS_MESSAGE');
          this.toasterService.show('success', title, message);
        },
        onError: (error) => {
          const title = this.translate.instant('EXPORT_SETTINGS.TOAST_ERROR_TITLE');
          const message = this.translate.instant('EXPORT_SETTINGS.TOAST_ERROR_MESSAGE');
          this.toasterService.show('error', title, message);
          console.error('Export error:', error);
        }
      }
    );
  }

  private async loadBrandsCombo(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
      const requestPayload: FilterRequestForCombo = buildRemoteDropdownRequest({
        pageIndex: evt.pageIndex,
        pageSize: evt.pageSize,
        search: evt.search
      });

      const response = await firstValueFrom(
        this.brandService.apiBrandsFilterComboPost(requestPayload)
      );

      this.brands = normalizeDropdownState(response as any, evt.pageIndex) as any;
      this.rebuildFiltersPanelOverrides();
  }

  async onSubmitEmailForm() {
    const payload: RetailerNotificationRequest = {
      email: this.emailForm.controls['email'].value
    };
    await firstValueFrom(this.retailersService.apiRetailersIdNotifyPost(this.emailForm.controls['id'].value, payload));
    this.onCancelEmailModal();
  }

  onCancelEmailModal() {
    this.displayEmailModalbox = false;
  }

  onOpenEmailModal(evt: any, data: any) {
    this.displayEmailModalbox = true;
    this.emailForm.patchValue({
      id: data.id,
      email: data.email
    });
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
